import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from '@clofo/i18n'
import { clofoApi } from '@clofo/data/api'
import { FormInput } from '@clofo/ui/controlled-field'
import { SubmitButton } from '@clofo/ui/submit-button'
import { Button } from '@clofo/ui/button'

export const ForgotPasswordForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Email carried over from the login password step (if the user arrived from there), used to
  // prefill the field and to restore that step when the user cancels.
  const { state } = useLocation()
  const email = (state as { email?: string } | null)?.email ?? ''

  const schema = z.object({ email: z.email({ error: t('errors.validEmail') }) })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email },
  })

  const onSubmit = async ({ email }: Values) => {
    try {
      await clofoApi.auth.resetPassword(email)
    } catch {
      form.setError('email', { message: t('login.errors.generic') })
    }
  }

  if (form.formState.isSubmitSuccessful) {
    return (
      <div className='flex flex-col w-full'>
        <p className='leading-relaxed' role='status' aria-live='polite'>
          {t('login.forgot.success')}
        </p>
        <Button
          size='lg'
          className='mt-8 w-fit'
          autoFocus
          onClick={() => navigate('/', { state: { email: form.getValues('email') } })}
        >
          {t('login.forgot.backToLogin')}
        </Button>
        {import.meta.env.DEV && (
          <Button variant='link' asChild className='mt-4 w-fit p-0'>
            {/* Dev-only stand-in for the emailed reset link — no email is sent against the MSW
                mocks, so this jumps straight to the reset form with placeholder params. */}
            <Link to='/passwords/reset?token=abc123-def456&uid=MQ'>Open reset link (dev only)</Link>
          </Button>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col w-full'>
      <p className='mb-8 leading-relaxed'>{t('login.forgot.description')}</p>

      <FormInput
        control={form.control}
        name='email'
        type='email'
        label={t('login.forgot.emailLabel')}
        isRequired
        autoComplete='email'
        autoFocus
        className={{ input: 'h-12' }}
      />

      <hr className='my-6 border-secondary' />

      <div className='flex gap-3'>
        <Button
          type='button'
          variant='secondary'
          size='lg'
          onClick={() => navigate('/', { state: { email } })}
        >
          {t('login.forgot.cancel')}
        </Button>
        <SubmitButton type='submit' size='lg' isSubmitting={form.formState.isSubmitting}>
          {t('login.forgot.submit')}
        </SubmitButton>
      </div>
    </form>
  )
}
